/**
 * Financial Aid Value Objects: Financial Status
 *
 * Sections 4-8: Employment, Income, Assets, Liabilities, Expenses
 *
 * @module financial-aid-domain/value-objects/financial-aid/financial-status
 */
import { z } from 'zod';
import { IncomeType, AssetType, ExpenseCategory } from '../../types/enums';
import { AddressSchema } from '@triedge/domain-core';

// ============================================================================
// Section 4: Employment Information
// ============================================================================

export const EmploymentEntrySchema = z.object({
  parentIndex: z.number().int().min(1).max(2), // Which parent (1 or 2)
  employerName: z.string().min(1),
  employerAddress: AddressSchema.nullable().optional(),
  position: z.string().nullable().optional(),
  startDate: z.number().int().nullable().optional(), // Unix timestamp
  annualSalary: z.number().nonnegative(),
  parsonageAmount: z.number().nonnegative().nullable().optional(),
  federalTax: z.number().nonnegative().nullable().optional(),
  stateTax: z.number().nonnegative().nullable().optional(),
  ficaTax: z.number().nonnegative().nullable().optional(),
  isPrimaryJob: z.boolean().default(true),
  hoursPerWeek: z.number().nonnegative().nullable().optional(),
});
export type EmploymentEntry = z.infer<typeof EmploymentEntrySchema>;

export const EmploymentInformationSchema = z.object({
  employments: z.array(EmploymentEntrySchema).default([]),
  isSelfEmployed: z.boolean().default(false),
  selfEmploymentDescription: z.string().nullable().optional(),
});
export type EmploymentInformation = z.infer<typeof EmploymentInformationSchema>;

// ============================================================================
// Section 5: Income Sources
// ============================================================================

export const IncomeEntrySchema = z.object({
  type: IncomeType,
  description: z.string().nullable().optional(),
  annualAmount: z.number().nonnegative(),
  isRecurring: z.boolean().default(true),
  frequency: z.enum(['weekly', 'biweekly', 'monthly', 'quarterly', 'annually', 'one_time']).default('annually'),
});
export type IncomeEntry = z.infer<typeof IncomeEntrySchema>;

export const IncomeSourcesSchema = z.object({
  entries: z.array(IncomeEntrySchema).default([]),
  totalAnnualIncome: z.number().nonnegative(), // Computed or entered
  previousYearIncome: z.number().nonnegative().nullable().optional(),
  incomeChangedSignificantly: z.boolean().default(false),
  incomeChangeExplanation: z.string().nullable().optional(),
});
export type IncomeSources = z.infer<typeof IncomeSourcesSchema>;

// ============================================================================
// Section 6: Assets
// ============================================================================

export const AssetEntrySchema = z.object({
  type: AssetType,
  description: z.string().nullable().optional(),
  currentValue: z.number().nonnegative(),
  isLiquid: z.boolean().default(false),
});
export type AssetEntry = z.infer<typeof AssetEntrySchema>;

export const AssetsSchema = z.object({
  entries: z.array(AssetEntrySchema).default([]),
  totalAssets: z.number().nonnegative(), // Computed or entered
  cashOnHand: z.number().nonnegative().default(0),
  checkingSavingsTotal: z.number().nonnegative().default(0),
  investmentsTotal: z.number().nonnegative().default(0),
  retirementTotal: z.number().nonnegative().default(0),
});
export type Assets = z.infer<typeof AssetsSchema>;

// ============================================================================
// Section 7: Liabilities
// ============================================================================

export const LiabilityEntrySchema = z.object({
  type: z.enum(['mortgage', 'car_loan', 'student_loan', 'credit_card', 'personal_loan', 'business_loan', 'other']),
  creditor: z.string().nullable().optional(),
  originalAmount: z.number().nonnegative().nullable().optional(),
  currentBalance: z.number().nonnegative(),
  monthlyPayment: z.number().nonnegative(),
  interestRate: z.number().nonnegative().nullable().optional(),
});
export type LiabilityEntry = z.infer<typeof LiabilityEntrySchema>;

export const LiabilitiesSchema = z.object({
  entries: z.array(LiabilityEntrySchema).default([]),
  totalLiabilities: z.number().nonnegative(), // Computed or entered
  totalMonthlyDebtPayments: z.number().nonnegative().default(0),
});
export type Liabilities = z.infer<typeof LiabilitiesSchema>;

// ============================================================================
// Section 8: Monthly Expenses
// ============================================================================

const MEDICAL_COVERAGE_TEXT_MAX_LENGTH = 4000;

export const ExpenseEntrySchema = z.object({
  category: ExpenseCategory,
  description: z.string().nullable().optional(),
  monthlyAmount: z.number().nonnegative(),
  sourceRequirementFieldKey: z.string().nullable().optional(),
});
export type ExpenseEntry = z.infer<typeof ExpenseEntrySchema>;

export const MedicalCoverageAnswerSchema = z.union([
  z.number().nonnegative(),
  z.string().max(MEDICAL_COVERAGE_TEXT_MAX_LENGTH),
]);
export type MedicalCoverageAnswer = z.infer<typeof MedicalCoverageAnswerSchema>;

export const MonthlyExpensesSchema = z.object({
  entries: z.array(ExpenseEntrySchema).default([]),
  housingCost: z.number().nonnegative().default(0), // Rent/mortgage
  utilitiesTotal: z.number().nonnegative().default(0),
  transportationTotal: z.number().nonnegative().default(0),
  foodTotal: z.number().nonnegative().default(0),
  healthInsurance: z.number().nonnegative().optional(),
  medicalCoverage: MedicalCoverageAnswerSchema.optional(),
  medicalCoverageDetails: z.string().max(MEDICAL_COVERAGE_TEXT_MAX_LENGTH).nullable().optional(),
  healthcareTotal: z.number().nonnegative().default(0),
  insuranceTotal: z.number().nonnegative().default(0),
  otherTotal: z.number().nonnegative().default(0),
  totalMonthlyExpenses: z.number().nonnegative(), // Computed
});
export type MonthlyExpenses = z.infer<typeof MonthlyExpensesSchema>;
